import { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { GlobalStyles, ThemeProvider } from "@mui/material";
import { landingColors } from "./landingColors";
import { createLandingTheme } from "./landingTheme";
import LandingNav from "./components/LandingNav";
import LandingFooter from "./components/LandingFooter";

const LANDING_FAVICON = `${process.env.PUBLIC_URL}/favicon-landing.png`;

// The icon index.html declares, read once at module load before anything can
// swap it. Snapshotting inside the effect instead would let a layout that
// mounts while another is still swapped capture the landing mark as the
// "original" and strand it on the rest of the app.
const APP_FAVICON =
  typeof document === "undefined"
    ? null
    : (document.querySelector("link[rel='icon']")?.getAttribute("href") ??
      null);

// Ref-counted, in the same spirit as the mobile app's tab-bar visibility.
// Navigating between "/" and a docs page unmounts one LandingLayout and mounts
// another in the same commit, so counting means the restore fires only when the
// last one is really gone, rather than relying on cleanup and setup running in
// a particular order.
let landingLayoutCount = 0;

const LandingLayout = ({ children }) => {
  const { pathname, hash } = useLocation();

  // Every page wearing the landing chrome shows the bare storefront mark in the
  // browser tab. The marketplace keeps the blue-tile favicon.
  useEffect(() => {
    const link = document.querySelector("link[rel='icon']");
    if (!link || APP_FAVICON === null) return undefined;

    landingLayoutCount += 1;
    link.setAttribute("href", LANDING_FAVICON);

    return () => {
      landingLayoutCount -= 1;
      if (landingLayoutCount === 0) link.setAttribute("href", APP_FAVICON);
    };
  }, []);

  // Footer links between pages would otherwise open the next page still
  // scrolled to the bottom. "instant" overrides html's smooth scroll-behavior.
  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, hash]);

  return (
    <ThemeProvider theme={createLandingTheme}>
      <GlobalStyles
        styles={{
          html: {
            scrollBehavior: "smooth",
            backgroundColor: landingColors.background,
          },
          body: { backgroundColor: landingColors.background },
        }}
      />
      <LandingNav />
      <main style={{ backgroundColor: landingColors.background }}>
        {children ?? <Outlet />}
      </main>
      <LandingFooter />
    </ThemeProvider>
  );
};

export default LandingLayout;
